// =============================================================================
// FindOverlay — Cmd+K palette across units. Click → /check-in/:id.
// =============================================================================
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBatteryUnits, unitName } from "../lib/useBatteryData.js";

export function FindOverlay({ open, onClose }) {
  const nav = useNavigate();
  const inputRef = useRef(null);
  const [q, setQ] = useState("");
  const { data: units } = useBatteryUnits();

  useEffect(() => {
    if (open) { setQ(""); setTimeout(() => inputRef.current?.focus(), 30); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); onClose(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const matches = (s) => !needle || String(s ?? "").toLowerCase().includes(needle);
    const uRows = units
      .filter((u) => matches(u.stockNumber) || matches(u.vin) || matches(u.make) || matches(u.model) || matches(u.year))
      .slice(0, 12)
      .map((u) => ({
        kind: "unit",
        id: u.id,
        name: unitName(u),
        sub: (u.stockNumber || u.vin || u.id.slice(0, 8)) + (Number.isFinite(Number(u.voltage)) ? ` · ${Number(u.voltage).toFixed(2)}V` : ""),
        meta: u.voltageType === "HV" ? "HV" : "12V",
        href: `/check-in/${u.id}`,
      }));
    const actions = [
      { kind: "action", id: "act-today", name: "Open Today dashboard", sub: "your customizable home", meta: "GO", href: "/today" },
      { kind: "action", id: "act-inv",   name: "Open Inventory",        sub: "every unit on the lot", meta: "GO", href: "/inventory" },
      { kind: "action", id: "act-add",   name: "Add a unit",             sub: "intake a new vehicle", meta: "GO", href: "/add-unit" },
      { kind: "action", id: "act-tv",    name: "Open TV mode",           sub: "lobby display",         meta: "GO", href: "/tv" },
    ].filter((a) => matches(a.name) || matches(a.sub));
    return { units: uRows, actions };
  }, [q, units]);

  function go(row) { onClose(); if (row?.href) nav(row.href); }
  if (!open) return null;

  return (
    <>
      <div className="find-back" onClick={onClose} />
      <div className="find-modal" role="dialog" aria-label="Find">
        <div className="find-search">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
          </svg>
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a unit by stock, VIN, make…" autoComplete="off" />
          <span className="find-kbd">ESC</span>
        </div>
        <div className="find-list">
          {results.units.length === 0 && results.actions.length === 0 && <div className="find-empty">Nothing matches that yet.</div>}
          {results.units.length > 0 && (
            <>
              <div className="find-section">UNITS</div>
              {results.units.map((r) => (
                <button key={r.id} type="button" className="find-row" onClick={() => go(r)}>
                  <span className="find-icon">
                    <svg width="18" height="12" viewBox="0 0 40 20" aria-hidden="true"><path d="M 4 16 L 8 9 L 14 7 L 30 7 L 34 9 L 36 14 L 38 16 L 6 16 Z" fill="currentColor" opacity="0.2" stroke="currentColor" strokeWidth="0.8"/><circle cx="12" cy="16" r="2" fill="none" stroke="currentColor" strokeWidth="0.8"/><circle cx="30" cy="16" r="2" fill="none" stroke="currentColor" strokeWidth="0.8"/></svg>
                  </span>
                  <span className="find-main">
                    <div className="find-name">{r.name}</div>
                    <div className="find-sub">{r.sub}</div>
                  </span>
                  <span className="find-meta">{r.meta}</span>
                </button>
              ))}
            </>
          )}
          {results.actions.length > 0 && (
            <>
              <div className="find-section">JUMP TO</div>
              {results.actions.map((r) => (
                <button key={r.id} type="button" className="find-row" onClick={() => go(r)}>
                  <span className="find-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                  </span>
                  <span className="find-main">
                    <div className="find-name">{r.name}</div>
                    <div className="find-sub">{r.sub}</div>
                  </span>
                  <span className="find-meta">{r.meta}</span>
                </button>
              ))}
            </>
          )}
        </div>
        <div className="find-foot">
          <span><span className="find-kbd">⏎</span> open</span>
          <span>Cmd+K from anywhere</span>
        </div>
      </div>
    </>
  );
}
